import _defineProperty from "/usr/src/app/node_modules/@babel/runtime/helpers/esm/defineProperty";
import _objectWithoutProperties from "/usr/src/app/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["isOpen", "direction", "tag", "active", "inNavbar", "styles", "disabled", "toggle", "dropup", "children", "itemsSelector"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import React, { memo, createContext, useEffect, useRef, useCallback, useMemo } from 'react';
import { Manager } from 'react-popper';
import classNames from 'classnames';
import { applyStylingHoc } from '../../lib/styling.js';
import baseStyles from './Dropdown.scss';
var keyCodes = {
  esc: 27,
  space: 32,
  tab: 9,
  up: 38,
  down: 40
};

function usePrevious(value) {
  // REF: TS: get rid of any
  var ref = useRef();
  useEffect(function () {
    // @ts-ignore
    ref.current = value;
  });
  return ref.current;
}

export var DropdownContext = createContext({});
export var Dropdown = function Dropdown(_ref) {
  var _ref$isOpen = _ref.isOpen,
      isOpen = _ref$isOpen === void 0 ? false : _ref$isOpen,
      _ref$direction = _ref.direction,
      direction = _ref$direction === void 0 ? 'down' : _ref$direction,
      _ref$tag = _ref.tag,
      tag = _ref$tag === void 0 ? 'div' : _ref$tag,
      _ref$active = _ref.active,
      active = _ref$active === void 0 ? false : _ref$active,
      _ref$inNavbar = _ref.inNavbar,
      inNavbar = _ref$inNavbar === void 0 ? false : _ref$inNavbar,
      _ref$styles = _ref.styles,
      styles = _ref$styles === void 0 ? {} : _ref$styles,
      disabled = _ref.disabled,
      toggle = _ref.toggle,
      dropup = _ref.dropup,
      children = _ref.children,
      itemsSelector = _ref.itemsSelector,
      rest = _objectWithoutProperties(_ref, _excluded);

  var prevIsOpen = usePrevious(isOpen);
  var mounted = useRef(false); // REF: Add proper type definition; <HTMLDivElement | null> introduces errors

  var containerRef = useRef(null);
  var makeToggle = useCallback(function (e) {
    if (disabled) {
      return e && e.preventDefault();
    }

    return toggle(e);
  }, [disabled, toggle]);
  var handleDocumentClick = useCallback(function (e) {
    if (e && (e.which === 3 || e.type === 'keyup' && e.which !== keyCodes.tab)) {
      return;
    }

    var container = getContainer();

    if ( // @ts-ignore
    container instanceof HTMLElement && // @ts-ignore
    container.contains(e.target) && container !== e.target && (e.type !== 'keyup' || e.which === keyCodes.tab)) {
      return;
    }

    makeToggle(e);
  }, [makeToggle]);
  var removeEvents = useCallback(function () {
    return ['click', 'touchstart', 'keyup'].forEach(function (event) {
      return document.removeEventListener(event, handleDocumentClick, true);
    });
  }, [handleDocumentClick]);
  var addEvents = useCallback(function () {
    return ['click', 'touchstart', 'keyup'].forEach(function (event) {
      return document.addEventListener(event, handleDocumentClick, true);
    });
  }, [handleDocumentClick]);
  var handleProps = useCallback(function () {
    if (isOpen) {
      addEvents();
    } else {
      removeEvents();
    }
  }, [addEvents, removeEvents, isOpen]);
  useEffect(function () {
    handleProps();
    return function () {
      return removeEvents();
    };
  }, [handleProps, removeEvents]);
  useEffect(function () {
    if (!mounted.current) {
      mounted.current = true;
    } else {
      if (prevIsOpen !== isOpen) {
        handleProps();
      }
    }
  });

  var getContainer = function getContainer() {
    return containerRef === null || containerRef === void 0 ? void 0 : containerRef.current;
  };

  var handleKeyDown = useCallback(function (e) {
    if ([keyCodes.esc, keyCodes.up, keyCodes.down, keyCodes.space].indexOf(e.which) === -1 || /button/i.test(e.target.tagName) && e.which === keyCodes.space || /input|textarea/i.test(e.target.tagName)) {
      return;
    }

    e.preventDefault();

    if (disabled) {
      return;
    }

    var container = getContainer();

    if (e.which === keyCodes.space && isOpen && container !== e.target) {
      e.target.click();
    }

    if (e.which === keyCodes.esc || !isOpen) {
      makeToggle(e); // @ts-ignore

      if (container instanceof HTMLElement) {
        // @ts-ignore
        var expanded = container.querySelector('[aria-expanded]');

        if (expanded) {
          expanded.focus();
        }
      }

      return;
    }

    var itemsSelectorData = itemsSelector || ".".concat(styles.menu, " .").concat(styles.item, ":not(.").concat(styles['is-disabled'], ")"); // @ts-ignore

    var items = container instanceof HTMLElement ? container.querySelectorAll(itemsSelectorData) : [];

    if (!items.length) {
      return;
    }

    var index = -1;

    for (var i = 0; i < items.length; i += 1) {
      if (items[i] === e.target) {
        index = i;
        break;
      }
    }

    if (e.which === keyCodes.up && index > 0) {
      index -= 1;
    }

    if (e.which === keyCodes.down && index < items.length - 1) {
      index += 1;
    }

    if (index < 0) {
      index = 0;
    }

    items[index].focus();
  }, [disabled, isOpen, itemsSelector, makeToggle, styles]);
  var directionData = direction === 'down' && dropup ? 'up' : direction;
  var classes = classNames(styles.dropdown, directionData !== 'down' && styles['drop' + directionData], isOpen && styles['is-visible']);
  var contextValue = useMemo(function () {
    return {
      toggle: toggle,
      isOpen: isOpen,
      direction: direction === 'down' && dropup ? 'up' : direction,
      inNavbar: inNavbar
    };
  }, [toggle, isOpen, direction, dropup, inNavbar]);
  return React.createElement(DropdownContext.Provider, {
    value: contextValue
  }, React.createElement("div", {
    className: classes,
    onKeyDown: handleKeyDown,
    ref: containerRef
  }, React.createElement(Manager, _objectSpread({
    tag: tag,
    active: active,
    itemsSelector: itemsSelector
  }, rest), children)));
};
export default applyStylingHoc(baseStyles)(memo(Dropdown));